import * as color from './color';

/**
 * these are the default browser element font sizes adjusted to our base font size of 16 px
 * this is used in the `prose` class of the typography plugin in order to revert tailwind to default css
 * styling follows DEN design system typography guidelines:
 * https://www.figma.com/design/agsyAxkSPm3ldeHFBVU8Pa/Markdown-Formatting-Showcase?node-id=0-1&t=sEFVehLmFR2FGBbe-1
 */
export const prose_css_defaults = {
  h1: {
    color: 'var(--Content-Default, #22282F) !important',
    fontStyle: 'normal !important',
    fontSize: 'var(--font-size-large, 18px) !important',
    fontWeight: 'var(--font-weight-600, 600) !important',
    lineHeight: '32px !important',
    margin: '.75em 0 !important',
  },
  h2: {
    color: 'var(--Content-Default, #22282F) !important',
    fontStyle: 'normal !important',
    fontSize: 'var(--font-size-large, 18px) !important',
    fontWeight: 'var(--font-weight-600, 600) !important',
    lineHeight: '32px !important',
    margin: '.75em 0 !important',
  },
  h3: {
    color: 'var(--Content-Default, #22282F) !important',
    fontStyle: 'normal !important',
    fontSize: 'var(--font-size-medium, 16px) !important',
    fontWeight: 'var(--font-weight-600, 600) !important',
    lineHeight: '28px !important',
    margin: '.83em 0 !important',
  },
  h4: {
    color: 'var(--Content-Default, #22282F) !important',
    fontStyle: 'normal !important',
    fontSize: 'var(--font-size-large, 16px) !important',
    fontWeight: 'var(--font-weight-600, 600) !important',
    margin: '1.12em 0 !important',
  },
  h5: {
    color: 'var(--Content-Default, #22282F) !important',
    fontStyle: 'normal !important',
    fontSize: 'var(--font-size-medium, 16px) !important',
    fontWeight: 'var(--font-weight-600, 600) !important',
    margin: '1.12em 0 !important',
  },
  h6: {
    color: 'var(--Content-Default, #22282F) !important',
    fontStyle: 'normal !important',
    fontSize: 'var(--font-size-small, 16px) !important',
    fontWeight: 'var(--font-weight-600, 600) !important',
    margin: '1.12em 0 !important',
  },
  p: {
    color: 'var(--Content-Default, #22282F) !important',
    fontStyle: 'normal !important',
    fontSize: 'var(--font-size-medium, 16px) !important',
    fontWeight: 'var(--font-weight-400, 400) !important',
    lineHeight: '24px !important',
    margin: '1.12em 0 !important',
  },
  span: {
    color: 'var(--Content-Default, #22282F) !important',
    fontStyle: 'normal !important',
    fontSize: 'var(--font-size-medium, 16px) !important',
    fontWeight: 'var(--font-weight-400, 400) !important',
    lineHeight: '24px !important',
    margin: '1.12em 0 !important',
  },
  a: {
    color: 'var(--Content-Primary, #144FFF) !important',
    fontStyle: 'normal !important',
    fontSize: 'var(--font-size-medium, 16px) !important',
    fontWeight: 'var(--font-weight-400, 400) !important',
    lineHeight: '24px !important',
    textDecoration: 'underline !important',
    textDecorationStyle: 'underline !important',
    textDecorationSkipInk: 'none !important',
    textDecordationThickness: 'auto !important',
    textUnderlineOffset: 'auto !important',
    textUnderlinePosition: 'from-font !important',
    margin: '1.12em 0 !important',
  },
  strong: {
    fontWeight: 'var(--font-weight-400, 600) !important',
  },
  em: { fontStyle: 'italic !important' },
  b: { fontWeight: 'var(--font-weight-400, 600) !important' },
  i: { fontStyle: 'italic !important' },
  u: { textDecoration: 'underline !important' },
  s: { textDecoration: 'line-through !important' },
  cite: { fontStyle: 'normal !important' },
  small: { fontSize: '13px !important' }, // Slightly smaller than the base
  mark: { fontSize: '16px !important' },
  del: { fontSize: '16px !important' },
  ins: { fontSize: '16px !important' },
  sub: { fontSize: '13px !important' }, // Smaller for subscript
  sup: { fontSize: '13px !important' }, // Smaller for superscript
  blockquote: {
    display: 'flex !important',
    padding: '16px 0px 16px 20px !important',
    flexDirection: 'column !important',
    alignItems: 'flex-start !important',
    alignSelf: 'stretch !important',
    color: 'var(--Content-Default, #22282F) !important',

    fontStyle: 'normal !important',
    fontSize: 'var(--font-size-medium, 16px) !important',
    fontWeight: 'var(--font-weight-400, 400) !important',
    lineHeight: '24px !important',
    borderLeft: '2px solid var(--Border-Subtle, #D3D8DF) !important',
  },
  pre: { fontSize: '14px !important' }, // Commonly smaller for code blocks
  code: { fontSize: '14px !important' },
  table: {
    fontSize: '16px !important',
    borderCollapse: 'collapse !important',
    width: '100% !important',
    borderWidth: '1px !important',
    borderStyle: 'solid !important',
    borderColor: color.border.get('default').toPropertyRef({ fallback: true }),
  },
  th: {
    fontSize: '16px !important',
    fontWeight: 'bold !important',
    borderWidth: '1px !important',
    borderStyle: 'solid !important',
    borderColor: color.border.get('default').toPropertyRef({ fallback: true }),
    padding: '10px !important',
  },
  td: {
    fontSize: '16px !important',
    borderWidth: '1px !important',
    borderStyle: 'solid !important',
    borderColor: color.border.get('default').toPropertyRef({ fallback: true }),
    padding: '10px !important',
  },
  caption: { fontSize: '14px !important', fontStyle: 'italic' }, // Optional
  ul: {
    fontSize: 'var(--font-size-medium, 16px) !important',
    listStyleType: 'disc !important',
    paddingLeft: '26px !important',
    margin: '10px 0px !important',
    lineHeight: '24px !important',
  },
  ol: {
    fontSize: 'var(--font-size-medium, 16px) !important',
    listStyleType: 'decimal !important',
    paddingLeft: '26px !important',
    margin: '10px 0px !important',
    lineHeight: '24px !important',
  },
  'ol ::marker': {
    color: '#1a1e26 !important',
  },
  li: {
    fontSize: 'var(--font-size-medium, 16px) !important',
    lineHeight: '24px !important',
    fontWeight: 'var(--font-weight-400, 400) !important',
    marginTop: '5px !important',
  },
  'li ::marker': {
    color: '#1a1e26 !important',
    paddingLeft: '6px !important',
  },
  dl: { fontSize: '16px !important' },
  dt: { fontSize: '16px !important', fontWeight: 'bold' },
  dd: { fontSize: '16px !important' },
};
